/**
 * Async testing utilities
 * @module @orchard9ai/testing-utils/utils/async
 */

import { act } from '@testing-library/react';

/**
 * Wait for a condition to be true
 */
export async function waitFor(
  condition: () => boolean | Promise<boolean>,
  options?: {
    timeout?: number;
    interval?: number;
  }
): Promise<void> {
  const { timeout = 1000, interval = 50 } = options || {};
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const result = await condition();
    if (result) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error(`Condition not met within ${timeout}ms`);
}

/**
 * Wait for next tick
 */
export function waitForNextTick(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * Wait for microtasks to complete
 */
export function flushMicrotasks(): Promise<void> {
  return new Promise((resolve) => {
    Promise.resolve().then(resolve);
  });
}

/**
 * Retry an async function
 */
export async function retry<T>(
  fn: () => Promise<T>,
  options?: {
    times?: number;
    delay?: number;
    onError?: (error: Error, attempt: number) => void;
  }
): Promise<T> {
  const { times = 3, delay = 100, onError } = options || {};

  for (let i = 0; i < times; i++) {
    try {
      return await fn();
    } catch (error) {
      if (onError) {
        onError(error as Error, i + 1);
      }

      if (i === times - 1) {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error('Retry failed');
}

/**
 * Create a deferred promise
 */
export function createDeferred<T>(): {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
} {
  let resolve!: (value: T) => void;
  let reject!: (error: Error) => void;

  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

/**
 * Wait for async updates with act
 */
export async function waitForAsync(fn?: () => void | Promise<void>): Promise<void> {
  await act(async () => {
    if (fn) {
      await fn();
    }
    await flushMicrotasks();
  });
}

/**
 * Create a timeout promise
 */
export function timeout(ms: number, message = `Timeout after ${ms}ms`): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(message)), ms);
  });
}

/**
 * Race with timeout
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message?: string
): Promise<T> {
  return Promise.race([promise, timeout(ms, message)]) as Promise<T>;
}

/**
 * Delay execution
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Create an abortable promise
 */
export function createAbortable<T>(fn: (signal: AbortSignal) => Promise<T>): {
  promise: Promise<T>;
  abort: () => void;
} {
  const controller = new AbortController();

  const promise = fn(controller.signal);

  return {
    promise,
    abort: () => controller.abort(),
  };
}

/**
 * Poll until condition is met
 */
export async function poll<T>(
  fn: () => T | Promise<T>,
  predicate: (value: T) => boolean,
  options?: {
    timeout?: number;
    interval?: number;
  }
): Promise<T> {
  const { timeout = 5000, interval = 100 } = options || {};
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const value = await fn();
    if (predicate(value)) {
      return value;
    }
    await delay(interval);
  }

  throw new Error(`Polling timed out after ${timeout}ms`);
}

/**
 * Create a promise that resolves after all pending timers
 */
export async function flushTimers(): Promise<void> {
  await act(async () => {
    if (typeof jest !== 'undefined' && jest.runAllTimers) {
      jest.runAllTimers();
    }
  });
}

/**
 * Advance timers by time
 */
export async function advanceTimersByTime(ms: number): Promise<void> {
  await act(async () => {
    if (typeof jest !== 'undefined' && jest.advanceTimersByTime) {
      jest.advanceTimersByTime(ms);
    }
  });
}
